import { Room, RoomEvent, RemoteTrack, RemoteTrackPublication, RemoteParticipant, Track } from "livekit-client";
import { StreamPersonaTransport, EngineStatus, FaceProfile, HardwareInfo, BillingAlert } from "./types";

export type { BillingAlert };

export class LiveKitTransport implements StreamPersonaTransport {
  readonly mode = "lite" as const;
  private room: Room | null = null;
  private statusListeners: Set<(status: EngineStatus) => void> = new Set();
  private alertListeners: Set<(alert: BillingAlert) => void> = new Set();
  private lastStatus: EngineStatus | null = null;
  private processedVideoTrack: RemoteTrack | null = null;
  private trackSubscribers: Set<(track: RemoteTrack) => void> = new Set();
  private isConnecting = false;

  get isConnected(): boolean {
    return !!this.room && this.room.state === "connected";
  }

  getRoom(): Room | null {
    return this.room;
  }

  async connect(credentials?: { roomName?: string; hostToken?: string; livekitUrl?: string }): Promise<void> {
    if (!credentials?.hostToken || !credentials?.livekitUrl) {
      console.warn("[LiveKitTransport] Missing credentials, skipping auto-connect");
      return;
    }
    if (this.room && this.room.state === "connected") {
      return;
    }
    if (this.isConnecting) return;

    this.isConnecting = true;
    try {
      if (this.room) {
        this.room.disconnect();
      }

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      room.on(RoomEvent.DataReceived, (payload: Uint8Array, _participant, _kind, topic) => {
        if (topic === "status") {
          try {
            const text = new TextDecoder().decode(payload);
            const status = JSON.parse(text) as EngineStatus;
            
            // Enrich with dynamic hardware discovery
            if (!status.hardware && status.gpu_name) {
              status.hardware = {
                gpu_name: status.gpu_name,
                vram_gb: status.gpu_name.includes("5090") ? 32 : 16,
                is_cloud: true,
                platform: "Linux (NVIDIA Blackwell sm_120)"
              };
            }
            this.lastStatus = status;
            this.statusListeners.forEach(cb => cb(status));
          } catch (e) {
            console.error("[LiveKitTransport] Error parsing status packet:", e);
          }
        } else if (topic === "alert" || topic === "billing") {
          try {
            const text = new TextDecoder().decode(payload);
            const alertData = JSON.parse(text) as BillingAlert;
            console.log("[LiveKitTransport] Received billing alert:", alertData);
            this.alertListeners.forEach(cb => {
              try {
                cb(alertData);
              } catch (e) {
                console.error("[LiveKitTransport] Error in alert listener callback:", e);
              }
            });
          } catch (e) {
            console.error("[LiveKitTransport] Error parsing alert/billing packet:", e);
          }
        }
      });


      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (participant.identity === "face-agent" && track.kind === Track.Kind.Video) {
          this.processedVideoTrack = track;
          this.trackSubscribers.forEach(cb => cb(track));
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        console.log("[LiveKitTransport] Room disconnected");
        this.room = null;
      });

      await room.connect(credentials.livekitUrl, credentials.hostToken);
      this.room = room;
      console.log(`[LiveKitTransport] Connected to room: ${credentials.roomName}`);

      // Request initial status packet
      setTimeout(() => {
        this.sendControlMessage("get_status");
      }, 300);

    } catch (err) {
      console.error("[LiveKitTransport] Connection error:", err);
      throw err;
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnect(): Promise<void> {
    if (this.room) {
      this.room.disconnect();
      this.room = null;
    }
  }

  async uploadFacePhoto(blob: Blob, name: string): Promise<FaceProfile | null> {
    if (!this.room || this.room.state !== "connected") {
      throw new Error("Cloud GPU node not yet connected via LiveKit WebRTC");
    }

    const compressedBytes = await this.compressToJpeg(blob, 512, 0.85);

    // If payload is <= 60 KB, transmit directly as raw binary
    if (compressedBytes.byteLength <= 60000) {
      await this.room.localParticipant.publishData(compressedBytes as any, {
        topic: "face_reference",
        reliable: true,
      });
    } else {
      // Chunk into safe 30KB blocks to strictly honor the 65,535-byte MTU limit
      const CHUNK_SIZE = 30000;
      const totalChunks = Math.ceil(compressedBytes.byteLength / CHUNK_SIZE);
      const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, compressedBytes.byteLength);
        const slice = compressedBytes.slice(start, end);
        let binary = "";
        for (let j = 0; j < slice.byteLength; j++) {
          binary += String.fromCharCode(slice[j]);
        }
        const base64Chunk = btoa(binary);

        const packet = JSON.stringify({
          type: "chunk",
          id: uploadId,
          index: i,
          total: totalChunks,
          data: base64Chunk,
        });

        const packetBytes = new TextEncoder().encode(packet);
        await this.room.localParticipant.publishData(packetBytes as any, {
          topic: "face_reference",
          reliable: true,
        });
      }
    }

    // Persist active profile info to local storage for quick restore
    const cleanName = name.replace(/\.[^.]+$/, "");
    try {
      localStorage.setItem("streampersona_active_profile", JSON.stringify({
        profile_id: `temp_${Date.now()}`,
        name: cleanName,
        is_active: true,
        updated_at: Date.now()
      }));
    } catch {
      // Ignore quota error
    }

    // Profile will be acknowledged asynchronously via status broadcast
    return {
      profile_id: `temp_${Date.now()}`,
      name: cleanName,
      is_active: true
    };
  }

  private async compressToJpeg(blob: Blob, maxDim = 512, quality = 0.85): Promise<Uint8Array> {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf))).catch(() => resolve(new Uint8Array(0)));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          async (compressedBlob) => {
            if (!compressedBlob) {
              const buf = await blob.arrayBuffer();
              resolve(new Uint8Array(buf));
              return;
            }
            const buf = await compressedBlob.arrayBuffer();
            resolve(new Uint8Array(buf));
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = async () => {
        URL.revokeObjectURL(url);
        const buf = await blob.arrayBuffer();
        resolve(new Uint8Array(buf));
      };
      img.src = url;
    });
  }

  async activateProfile(profileId: string): Promise<void> {
    await this.sendControlMessage("activate_profile", { profile_id: profileId });
  }

  async setToggles(toggles: Record<string, unknown>): Promise<void> {
    await this.sendControlMessage("set_toggles", toggles);
  }

  async clearSession(): Promise<void> {
    try {
      const { clearFaceVault } = await import("../faceVault");
      await clearFaceVault();
    } catch {
      // ignore
    }
    try {
      localStorage.removeItem("streampersona_active_profile");
      localStorage.removeItem("streampersona_profiles");
    } catch {
      // ignore
    }
    await this.sendControlMessage("clear_session");
  }

  async getHardware(): Promise<HardwareInfo> {
    if (this.lastStatus?.hardware) {
      return this.lastStatus.hardware;
    }
    const gpuName = this.lastStatus?.gpu_name || "NVIDIA GeForce RTX 5090 (Blackwell)";
    return {
      gpu_name: gpuName,
      vram_gb: 32,
      is_cloud: true,
      platform: "Cloud Node (Vast.ai)"
    };
  }

  async toggleVirtualCam(_enable: boolean): Promise<boolean> {
    // In Lite mode, virtual camera is driven via OS display capture or native bridge
    console.info("[LiveKitTransport] Virtual camera active for WebRTC output");
    return true;
  }

  async toggleVirtualMic(_enable: boolean): Promise<boolean> {
    console.info("[LiveKitTransport] Virtual mic routed via local VB-Cable audio loopback");
    return true;
  }

  onStatus(callback: (status: EngineStatus) => void): () => void {
    this.statusListeners.add(callback);
    if (this.lastStatus) {
      callback(this.lastStatus);
    }
    return () => this.statusListeners.delete(callback);
  }

  onAlert(callback: (alert: BillingAlert) => void): () => void {
    this.alertListeners.add(callback);
    return () => this.offAlert(callback);
  }

  offAlert(callback: (alert: BillingAlert) => void): void {
    this.alertListeners.delete(callback);
  }

  emitAlert(alert: BillingAlert): void {
    this.alertListeners.forEach(cb => {
      try {
        cb(alert);
      } catch (err) {
        console.error("[LiveKitTransport] Error in emitAlert listener:", err);
      }
    });
  }


  onProcessedTrack(callback: (track: RemoteTrack) => void): () => void {
    this.trackSubscribers.add(callback);
    if (this.processedVideoTrack) {
      callback(this.processedVideoTrack);
    }
    return () => this.trackSubscribers.delete(callback);
  }

  private async sendControlMessage(action: string, payload: Record<string, unknown> = {}): Promise<boolean> {
    if (this.room && this.room.localParticipant && this.room.state === "connected") {
      try {
        const msg = JSON.stringify({ action, payload });
        const encoder = new TextEncoder();
        await this.room.localParticipant.publishData(encoder.encode(msg), {
          topic: "control",
          reliable: true,
        });
        return true;
      } catch (e) {
        console.error("[LiveKitTransport] Failed to send control packet:", e);
      }
    }
    return false;
  }
}
